import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import * as config from "../Config/common.config";

interface APIRequestParams {
  url: string;
  method: string;
  params?: Record<string, any>;
}

/**
 * Typed API response - discriminated union for type-safe error handling
 */
interface ApiSuccessResponse<T = any> {
  data: T;
  error?: never;
}

interface ApiErrorResponse {
  data?: never;
  error: {
    message: string;
    code?: string | number;
    status?: number;
  };
}

type ApiResponse<T = any> = ApiSuccessResponse<T> | ApiErrorResponse;

const APIRequest = async <T = any>(
  { url, method }: APIRequestParams,
  data: any = null,
  params: any = null
): Promise<ApiResponse<T>> => {
  const apiurl = `${process.env.REACT_APP_API_ENDPOINT}${url}`;

  try {
    const token = config.getSessionToken();

    // Build headers - only add auth if token exists (SECURITY FIX)
    const headers: Record<string, string> = {};
    if (token && token.trim()) {
      headers.Authorization = `Bearer ${token}`;
    }

    // Prepare the request payload
    const payload: AxiosRequestConfig = {
      url: apiurl,
      method,
      headers,
    };

    // Add params and data if available
    if (params) payload.params = params;
    if (data) payload.data = data;

    // Make the API call
    const response: AxiosResponse<T> = await axios(payload);
    return { data: response.data };
  } catch (error: any) {
    const status = error?.response?.status;
    const errorData = error?.response?.data;

    // Handle 401/403 (unauthorized/forbidden) - logout user
    if ([401, 403].includes(status)) {
      config.logout();
    }

    // Return structured error response
    return {
      error: {
        message: errorData?.message ?? "Server is unreachable",
        code: errorData?.code,
        status,
      },
    };
  }
};

export default APIRequest;
